/**
 * The front screen (U18, room list and menu U26).
 *
 * This is what the game opens on. It used to boot straight into a skirmish,
 * which meant single player was the only mode you could find without knowing
 * to add `?mp=1` to the URL — so multiplayer effectively did not exist for
 * anybody who had not been told about it.
 *
 * **The match code is gone from the player's path.** It used to be the lobby:
 * press HOST, copy eight characters, send them to somebody, both type them in
 * and press JOIN. That is three steps and two chances to get it wrong before
 * anyone has played anything — and pressing HOST a second time silently
 * invalidated the code already sent, which is the failure that made the whole
 * thing feel unreliable rather than merely fiddly.
 *
 * What replaces it is a list of open rooms, refreshed while you look at it.
 * Click a room to join it; press NEW ROOM if there is nothing to click. Codes
 * still exist — they are the Durable Object's key and what the resume token is
 * keyed on — but a player never sees or types one.
 *
 * The reconnecting indicator matters more than it looks. A lockstep client that
 * stalls is behaving correctly, but a frozen screen with no explanation reads
 * as a crash, so the stall has to be visible and named.
 */

import { NetworkRelay, type NetworkEvent } from '../net/network.js'
import { listRooms, createRoom, type RoomSummary } from '../net/rooms.js'
import type { CommandWorld } from '../net/commands.js'

export interface LobbyOptions {
  root: HTMLElement
  /** WebSocket endpoint. The dev relay and the Durable Object share a shape. */
  relayUrl: string
  /** Called once the relay reports the match starting. */
  onStart(relay: NetworkRelay, slots: Array<{ faction: number; team: number }>): void
  /** Called when the player picks single player. Omitted, the button is hidden. */
  onSinglePlayer?(aiLevel?: string): void
}

const TOKEN_KEY = 'bluealert.resume'
/** Reconnect attempts before the lobby hands the problem back to the player. */
const MAX_RETRIES = 5
const RETRY_DELAY_MS = 1500
/**
 * Reconnect attempts *during* a match, and how long the backoff may grow to.
 *
 * More patient than the lobby's, because there is a game to come back to. A
 * redeployed relay is unreachable for a few seconds and then fine, so the
 * budget is sized to outlast that comfortably.
 */
const MATCH_RETRIES = 12
const MAX_RETRY_DELAY_MS = 6000
/**
 * How often the open-room list is refreshed.
 *
 * Fast enough that a room somebody else just made appears while you are still
 * looking at the screen, which is the whole point of a list. It is one Durable
 * Object read, and polling stops the moment the lobby is hidden.
 */
const REFRESH_MS = 2500

/** Refusals that will not change if you ask again. */
const FINAL_REASONS = new Set([
  'no-such-match',
  'bad-code',
  'bad-token',
  'lobby-full',
  'match-in-progress',
  'RING_LOST',
])

/** What each refusal means, and what to do about it. */
const REFUSALS: Record<string, string> = {
  'no-such-match': 'That room has closed. Pick another, or open a new one.',
  'bad-code': 'That room is not valid. Open a new one.',
  'bad-token': 'That room does not recognise this browser. Open a new one.',
  'lobby-full': 'Somebody got there first — that room is full.',
  'match-in-progress': 'That match has already started without you.',
  RING_LOST: 'That match has ended. Open a new room.',
}

export class Lobby {
  private readonly root: HTMLElement
  private readonly panel: HTMLDivElement
  private readonly status: HTMLDivElement
  private readonly roomList: HTMLUListElement
  private readonly onlineLine: HTMLDivElement
  private readonly createButton: HTMLButtonElement
  private readonly leaveButton: HTMLButtonElement
  /** The room this client is sitting in, once it has been seated. */
  private seated: { code: string; name?: string } | null = null
  /** Set while deliberately giving up a seat, so the socket close is expected. */
  private leaving = false
  private relay: NetworkRelay | null = null
  private started = false
  private retries = 0
  private fatal = false
  private busy = false
  private refreshTimer = 0
  private inFlight: AbortController | null = null
  /** Set once the relay has no room list, so the fallback is only announced once. */
  private directoryless = false

  constructor(private readonly opts: LobbyOptions) {
    this.root = opts.root
    this.panel = document.createElement('div')
    this.panel.className = 'ba-lobby interactive'
    this.panel.innerHTML = `<h1>BLUE ALERT</h1>`

    if (opts.onSinglePlayer) {
      const row = document.createElement('div')
      row.className = 'ba-solo-row'
      const solo = document.createElement('button')
      solo.className = 'ba-solo'
      solo.textContent = 'SINGLE PLAYER'

      // Passive is deliberately last and labelled as testing: it is not a
      // difficulty below easy but the absence of an opponent, and a player
      // reaching for "easiest" should land on EASY, not on a game that cannot
      // be lost or won.
      const level = document.createElement('select')
      level.className = 'ba-ai-level'
      for (const [value, label] of [
        ['easy', 'EASY'],
        ['normal', 'NORMAL'],
        ['hard', 'HARD'],
        ['passive', 'PASSIVE (TESTING)'],
      ]) {
        const option = document.createElement('option')
        option.value = value
        option.textContent = label
        if (value === 'normal') option.selected = true
        level.append(option)
      }
      const note = document.createElement('p')
      note.className = 'hint'
      level.addEventListener('change', () => {
        note.textContent =
          level.value === 'passive'
            ? 'The enemy never builds or attacks. For trying things out, not for playing.'
            : ''
      })

      solo.addEventListener('click', () => {
        this.dispose()
        opts.onSinglePlayer?.(level.value)
      })
      row.append(solo, level)
      this.panel.append(row, note)
    }

    const heading = document.createElement('p')
    heading.className = 'hint'
    heading.textContent = 'Or play someone: pick a room, or open one and wait.'
    this.panel.append(heading)

    this.roomList = document.createElement('ul')
    this.roomList.className = 'ba-rooms'

    const row = document.createElement('div')
    row.className = 'ba-lobby-row'
    this.createButton = document.createElement('button')
    this.createButton.textContent = 'NEW ROOM'
    this.createButton.addEventListener('click', () => void this.createAndJoin())
    const refresh = document.createElement('button')
    refresh.textContent = 'REFRESH'
    refresh.addEventListener('click', () => void this.refresh())
    row.append(this.createButton, refresh)

    this.leaveButton = document.createElement('button')
    this.leaveButton.textContent = 'LEAVE ROOM'
    this.leaveButton.style.display = 'none'
    this.leaveButton.addEventListener('click', () => this.leave())
    row.append(this.leaveButton)

    this.status = document.createElement('div')
    this.status.className = 'ba-lobby-status'
    this.onlineLine = document.createElement('div')
    this.onlineLine.className = 'ba-online'

    this.panel.append(this.roomList, row, this.status, this.onlineLine)
    this.root.append(this.panel)

    this.renderRooms(null)
    void this.refresh()
    this.startPolling()

    // A stored resume token makes a page reload a reconnect rather than a
    // forfeit, which is the difference between a hiccup and a lost match.
    const saved = readToken()
    if (saved) this.offerResume(saved)
  }

  // --- the room list --------------------------------------------------------

  private startPolling(): void {
    if (this.refreshTimer !== 0) return
    this.refreshTimer = window.setInterval(() => {
      if (!this.started && !this.busy) void this.refresh()
    }, REFRESH_MS)
  }

  private stopPolling(): void {
    if (this.refreshTimer === 0) return
    window.clearInterval(this.refreshTimer)
    this.refreshTimer = 0
  }

  private async refresh(): Promise<void> {
    this.inFlight?.abort()
    const controller = new AbortController()
    this.inFlight = controller
    try {
      // Stop heartbeating once seated. From then on the room's own occupancy
      // is what counts this client, and sending both would show one player as
      // two for as long as the heartbeat took to expire.
      const listing = await listRooms(this.opts.relayUrl, controller.signal, this.seated === null)
      this.directoryless = false
      this.renderRooms(listing.rooms)
      this.renderOnline(listing.online)
      this.renderSeated(listing.rooms)
    } catch (err) {
      if (controller.signal.aborted) return
      // The dev relay is a single fixed-code match with no HTTP surface. Rather
      // than tell a developer their relay is broken, offer the one room it is.
      this.renderRooms(null)
      if (!this.directoryless) {
        this.directoryless = true
        console.info('[bluealert] no room directory on this relay', err)
      }
    }
  }

  /**
   * How many people have the game open.
   *
   * The number that matters on an empty list is not "how many rooms" but "is
   * anybody else here" — a player looking at nothing has no way to tell a
   * quiet moment from a broken relay, and will conclude the latter.
   */
  private renderOnline(online: number): void {
    this.onlineLine.textContent =
      online <= 1 ? 'Nobody else is online right now.' : `${online} players online.`
  }

  /**
   * What is happening in the room this client is sitting in.
   *
   * Read from the directory rather than from our own socket, on purpose. The
   * question a player waiting in a lobby is actually asking is "is the other
   * person still there", and our socket cannot answer it: it knows what the
   * relay has told *us*, and a peer whose connection died without closing
   * looks identical to one who has not arrived. The directory is counting
   * sockets the relay can still see.
   */
  private renderSeated(rooms: RoomSummary[]): void {
    if (!this.seated || this.started) return
    const room = rooms.find((r) => r.code === this.seated?.code)
    const name = this.seated.name ?? 'the room'
    if (!room) {
      // Our own room is not in the list. Either it has just been made and we
      // are the first poll, or it has closed under us.
      this.setStatus(`Waiting in ${name}…`)
      return
    }
    this.setStatus(
      `Waiting in ${name} — ${room.players} of ${room.capacity} players here.` +
        (room.players < room.capacity ? ' The match starts when it is full.' : ''),
    )
  }

  /**
   * Get back into a match whose connection dropped.
   *
   * The *same* relay object is reused rather than a fresh one: the running
   * game holds a reference to it, and a new one would arrive with no history,
   * no token and no way to prove it owns the seat.
   *
   * Backoff is linear and capped. A relay that has just been redeployed is
   * unreachable for a few seconds, and hammering it does not bring it back any
   * sooner.
   */
  private reconnectInMatch(): void {
    const relay = this.relay
    if (!relay || this.leaving) return
    if (this.retries >= MATCH_RETRIES) {
      this.onGameEvent?.({ kind: 'error', reason: 'reconnect-failed' })
      return
    }
    this.retries++
    const delay = Math.min(RETRY_DELAY_MS * this.retries, MAX_RETRY_DELAY_MS)
    window.setTimeout(() => {
      if (!this.leaving) relay.reconnect()
    }, delay)
  }

  /**
   * Give up the seat and go back to the list.
   *
   * `leaving` matters more than it looks. Disposing the relay closes the
   * socket, which arrives as a `closed` event — and the handler for that is
   * written for a connection that dropped on its own, so it announced
   * "reconnecting" and rejoined the room the player had just walked out of.
   */
  private leave(): void {
    this.leaving = true
    this.relay?.dispose()
    this.relay = null
    this.seated = null
    this.fatal = false
    this.retries = 0
    this.leaveButton.style.display = 'none'
    this.createButton.disabled = false
    this.setStatus('')
    void this.refresh()
  }

  private renderRooms(rooms: RoomSummary[] | null): void {
    this.roomList.replaceChildren()

    if (rooms === null) {
      const fallback = this.fallbackRoom()
      if (fallback) {
        this.roomList.append(this.roomRow(fallback))
        return
      }
      this.roomList.append(this.emptyRow('Could not reach the relay.'))
      return
    }

    if (rooms.length === 0) {
      this.roomList.append(this.emptyRow('No rooms open. Press NEW ROOM to start one.'))
      return
    }
    for (const room of rooms) this.roomList.append(this.roomRow(room))
  }

  private emptyRow(text: string): HTMLLIElement {
    const li = document.createElement('li')
    li.className = 'ba-room empty'
    li.textContent = text
    return li
  }

  private roomRow(room: RoomSummary): HTMLLIElement {
    const li = document.createElement('li')
    li.className = `ba-room${room.joinable ? '' : ' full'}`

    const name = document.createElement('span')
    name.className = 'ba-room-name'
    name.textContent = room.name

    const count = document.createElement('span')
    count.className = 'ba-room-count'
    count.textContent = room.started
      ? 'in progress'
      : `${room.players}/${room.capacity} waiting`

    const button = document.createElement('button')
    button.className = 'ba-room-join'
    button.textContent = room.joinable ? 'JOIN' : 'FULL'
    button.disabled = !room.joinable
    button.addEventListener('click', () => this.join(room.code, room.name))

    li.append(name, count, button)
    return li
  }

  /**
   * The room a relay with no directory is serving.
   *
   * The dev relay hosts exactly one match and puts its code in the URL the page
   * was pointed at, so there is nothing to look up.
   */
  private fallbackRoom(): RoomSummary | null {
    try {
      const code = new URL(this.opts.relayUrl.replace(/^ws/, 'http')).searchParams.get('code')
      if (!code) return null
      return { code, name: 'Local match', players: 0, capacity: 2, started: false, joinable: true }
    } catch {
      return null
    }
  }

  private async createAndJoin(): Promise<void> {
    if (this.busy) return
    this.busy = true
    this.createButton.disabled = true
    this.setStatus('Opening a room…')
    try {
      const room = await createRoom(this.opts.relayUrl)
      await this.refresh()
      // Straight in. Creating a room and then having to find and click it was
      // the second half of the old two-step, and it is no better than the code
      // box it replaced.
      this.join(room.code, room.name)
    } catch (err) {
      this.setStatus(
        `Could not open a room (${err instanceof Error ? err.message : String(err)}).`,
        true,
      )
    } finally {
      this.busy = false
      this.createButton.disabled = false
    }
  }

  /** Pre-fill the status with a one-click way back into an interrupted match. */
  private offerResume(saved: { code: string; name?: string }): void {
    this.setStatus('')
    const resume = document.createElement('button')
    resume.className = 'ba-resume'
    resume.textContent = `REJOIN ${saved.name ?? 'your last match'}`
    resume.addEventListener('click', () => this.join(saved.code, saved.name))
    this.status.replaceChildren(resume)
  }

  // --- joining --------------------------------------------------------------

  join(code: string, name?: string): void {
    // Cleared here rather than on a timer after leaving. A socket close is
    // delivered asynchronously and can arrive long after the click that caused
    // it, so any timeout short enough to be invisible is also short enough to
    // lose the race — and losing it puts the player straight back into the
    // room they just left.
    this.leaving = false
    this.fatal = false
    if (!/^[A-HJ-NP-Z2-9]{8}$/.test(code)) {
      this.setStatus('That room is no longer valid.')
      return
    }
    const saved = readToken()
    const token = saved && saved.code === code ? saved.token : undefined

    this.setStatus(`Joining ${name ?? 'the room'}…`)
    const world: CommandWorld = { isLive: () => true, ownerOf: () => -1 }
    const relay = new NetworkRelay({
      url: `${this.opts.relayUrl}${this.opts.relayUrl.includes('?') ? '&' : '?'}code=${code}`,
      code,
      world,
      ...(token ? { token } : {}),
      onEvent: (e) => this.onEvent(code, name, e),
    })
    this.relay = relay
    relay.connect()
  }

  /**
   * Where relay events go once the match has started and this panel is hidden.
   * Set by whoever booted the game.
   */
  onGameEvent: ((event: NetworkEvent) => void) | null = null

  private onEvent(code: string, name: string | undefined, event: NetworkEvent): void {
    if (this.leaving) return
    if (this.started) this.onGameEvent?.(event)
    switch (event.kind) {
      case 'connected':
        this.retries = 0
        if (this.started) {
          // Back in. The game is still running behind this panel, so nothing
          // is rebuilt — the relay picks its own barrier back up.
          this.onGameEvent?.(event)
          break
        }
        writeToken(code, event.token, name)
        this.seated = { code, ...(name ? { name } : {}) }
        this.leaveButton.style.display = ''
        this.setStatus(`Waiting in ${name ?? 'the room'}…`)
        // Ask straight away rather than waiting out the poll interval, so the
        // occupancy line is right the moment the panel changes.
        void this.refresh()
        break
      case 'start':
        this.started = true
        this.seated = null
        this.stopPolling()
        this.setStatus('Match starting…')
        this.hide()
        if (this.relay) this.opts.onStart(this.relay, event.slots)
        break
      case 'stalled':
        this.setStatus(`Waiting for the other player (tick ${event.tick})…`, true)
        break
      case 'resumed':
        this.setStatus('')
        break
      case 'late':
        this.setStatus(`Player ${event.slot} is falling behind…`, true)
        break
      case 'dropped':
        this.setStatus(`Player ${event.slot} was dropped (${event.reason}).`, true)
        break
      case 'desync':
        this.show()
        this.setStatus(
          `Desync detected between ticks ${event.from} and ${event.to}. The match has ended.`,
          true,
        )
        break
      case 'error':
        this.show()
        // A refusal is final: retrying against a room that has closed, ended, or
        // filled up produces four more identical refusals and a misleading
        // "reconnecting" message on top of them.
        if (FINAL_REASONS.has(event.reason)) {
          this.fatal = true
          this.retries = 0
          if (event.reason === 'no-such-match') clearToken()
          this.setStatus(REFUSALS[event.reason] ?? `Relay error: ${event.reason}`, true)
          // Whatever went wrong, the list is the way out of it.
          void this.refresh()
          break
        }
        this.setStatus(`Relay error: ${event.reason}`, true)
        break
      case 'closed':
        // Before the match starts, a dropped connection is worth retrying on
        // the player's behalf: they are sitting on a lobby screen with nothing
        // to do but press the same button again. A *refused* connection is not
        // — the status already says why, and overwriting it with "reconnecting"
        // is how a player ends up staring at a countdown for a match that no
        // longer exists.
        if (this.fatal) break
        if (this.started) {
          // A match no longer ends because the socket did. The relay keeps the
          // seat, the client keeps its recent submissions, and reconnecting
          // with the resume token rebuilds the barrier where it left off — so
          // a deploy, a dropped wifi connection or a closed laptop lid is a
          // pause rather than a forfeit.
          this.reconnectInMatch()
          break
        }
        if (this.retries >= MAX_RETRIES) {
          this.setStatus('Disconnected. Pick a room to try again.', true)
          break
        }
        this.retries++
        this.setStatus(`Connection dropped — reconnecting (${this.retries}/${MAX_RETRIES})…`, true)
        window.setTimeout(() => {
          if (!this.started) this.join(code, name)
        }, RETRY_DELAY_MS)
        break
      default:
        break
    }
  }

  private setStatus(text: string, bad = false): void {
    this.status.textContent = text
    this.status.classList.toggle('bad', bad)
  }

  show(): void {
    this.panel.style.display = 'flex'
    if (!this.started) {
      this.startPolling()
      void this.refresh()
    }
  }

  hide(): void {
    this.panel.style.display = 'none'
    this.stopPolling()
  }

  dispose(): void {
    this.stopPolling()
    this.inFlight?.abort()
    this.relay?.dispose()
    this.panel.remove()
  }
}

function readToken(): { code: string; token: string; name?: string } | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { code?: unknown; token?: unknown; name?: unknown }
    if (typeof parsed.code !== 'string' || typeof parsed.token !== 'string') return null
    return {
      code: parsed.code,
      token: parsed.token,
      ...(typeof parsed.name === 'string' ? { name: parsed.name } : {}),
    }
  } catch {
    return null
  }
}

/**
 * Forget a resume token whose room is gone.
 *
 * The stored token puts a REJOIN button on the screen, which is how a player
 * ends up pressing it on a room that closed hours ago and being told, five
 * times, that their connection dropped.
 */
function clearToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY)
  } catch {
    // Private browsing. Nothing was stored to begin with.
  }
}

function writeToken(code: string, token: string, name?: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify({ code, token, name }))
  } catch {
    // Private browsing, or storage disabled. Reconnect simply will not work.
  }
}
